// frontend/src/components/LiveScoresStrip.js
//
// Featured live & upcoming scores strip for the web dashboard. Reads our own
// backend endpoint (which proxies + caches ESPN's free feed), auto-refreshes,
// and shows a scroll-driven Today/Tomorrow header - the web counterpart of
// mobile/src/components/LiveScoresStrip.js.
//
// Kept in step with its counterpart by hand - these two files are independent
// npm packages with no shared module, so `localDayOffset`, `dayLabelOf`,
// `isWithinWindow` and `groupByDay` must stay IDENTICAL in both. Change one,
// change the other.
import React, { useState, useEffect, useRef } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const REFRESH_MS = 30000;

// Card + group layout. Group start offsets are computed from these so the sticky
// header can be positioned without measuring every card.
const CARD_WIDTH = 158;
const CARD_GAP = 10;
const GROUP_GAP = 24;
const STRIP_INSET = 0;

// Kickoff time for upcoming games, in the browser's locale/timezone. Shows the
// real date once a game is more than a week out (year too if not this year).
const formatKickoff = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return time;
  const daysAway = (d - now) / (1000 * 60 * 60 * 24);
  if (daysAway < 7) return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  const dateOpts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) dateOpts.year = 'numeric';
  return `${d.toLocaleDateString([], dateOpts)}, ${time}`;
};

// Local-day offset of a game in the browser's timezone: 0 = today (or live),
// 1 = tomorrow, 2+ = later. Used both to label and to strictly bound the strip.
const localDayOffset = (g) => {
  if (!g || g.state === 'in') return 0;
  const d = new Date(g.startTime);
  if (isNaN(d.getTime())) return 0;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfGameDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((startOfGameDay - startOfToday) / 86400000);
};

// How many days ahead the strip shows. MUST match the backend's
// UpcomingWindowDays (LiveScoresService.cs) - the clients apply the cap a second
// time, so a wider server window alone is invisible and a wider client window
// alone shows nothing. 3 days because weekly sports put their next game that far
// out: at 1 day the NFL tab showed bettable Sunday games above an empty strip.
const WINDOW_DAYS = 3;

// Fixed names rather than toLocaleDateString: no Intl dependency (Hermes builds
// vary) and deterministic in tests.
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const dayLabelOf = (g) => {
  const o = localDayOffset(g);
  if (o <= 0) return 'Today';
  if (o === 1) return 'Tomorrow';
  // Past tomorrow, name the weekday. Calling a Sunday game "Tomorrow" on a Friday
  // was the bug that showed up once the window widened for NFL.
  const d = new Date(g.startTime);
  return isNaN(d.getTime()) ? 'Later' : WEEKDAY_LABELS[d.getDay()];
};

// Bounded in the browser's LOCAL timezone. The backend window is UTC-based and now also
// reaches a day BACK for ESPN's US-Eastern bucketing, so this is what keeps a
// stale or far-out fixture from rendering under a wrong header.
const isWithinWindow = (g) => {
  const o = localDayOffset(g);
  return o >= 0 && o <= WINDOW_DAYS;
};

// Group games into ordered day sections (soonest day first), preserving the
// backend's live-first / soonest-first ordering within each day.
const groupByDay = (games) => {
  const groups = [];
  for (const g of games) {
    const label = dayLabelOf(g);
    let grp = groups.find((x) => x.label === label);
    if (!grp) {
      grp = { label, games: [] };
      groups.push(grp);
    }
    grp.games.push(g);
  }
  return groups;
};

const LiveDot = () => (
  <span
    style={{
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: '#ef4444',
      display: 'inline-block',
      animation: 'lsPulse 1.4s ease-in-out infinite',
    }}
  />
);

const TeamRow = ({ team, showScore, dim }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
    {team?.logo ? (
      <img src={team.logo} alt="" style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain' }} />
    ) : (
      <span style={{ width: 20, height: 20, marginRight: 8, borderRadius: 10, background: '#1f2937', display: 'inline-block' }} />
    )}
    <span
      style={{
        color: dim ? '#6b7280' : '#fff',
        fontSize: 14,
        fontWeight: 600,
        flex: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {team?.abbreviation || team?.shortName || team?.name || ''}
    </span>
    {showScore && (
      <span style={{ color: dim ? '#6b7280' : '#fff', fontSize: 15, fontWeight: 800, marginLeft: 8 }}>
        {team?.score ?? ''}
      </span>
    )}
  </div>
);

const GameCard = ({ game }) => {
  const isLive = game.state === 'in';
  const isFinal = game.state === 'post';
  const showScore = isLive || isFinal;

  let homeDim = false;
  let awayDim = false;
  if (isFinal) {
    const h = parseInt(game.home?.score, 10);
    const a = parseInt(game.away?.score, 10);
    if (!isNaN(h) && !isNaN(a) && h !== a) {
      homeDim = h < a;
      awayDim = a < h;
    }
  }

  return (
    <div
      style={{
        width: CARD_WIDTH,
        flex: '0 0 auto',
        background: '#111827',
        borderRadius: 14,
        border: '1px solid #1f2937',
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span
          style={{
            color: '#9ca3af',
            fontSize: 11,
            fontWeight: 600,
            flex: 1,
            marginRight: 6,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {game.league}
        </span>
        {isLive ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <LiveDot />
            <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 700 }}>{game.statusDetail || 'LIVE'}</span>
          </span>
        ) : (
          <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {isFinal ? (game.statusDetail || 'FT') : formatKickoff(game.startTime)}
          </span>
        )}
      </div>

      <TeamRow team={game.home} showScore={showScore} dim={homeDim} />
      <TeamRow team={game.away} showScore={showScore} dim={awayDim} />
    </div>
  );
};

// `league` is the DISPLAY LABEL of the single league to show (e.g. "NFL",
// "Premier League") - it matches game.league, not the odds-API sport key and not
// the coarser game.sport. Falsy renders nothing, which is also what an empty
// result does, so the dashboard never shows a stranded empty band.
const LiveScoresStrip = ({ league }) => {
  const [games, setGames] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [scrollX, setScrollX] = useState(0);
  // Measured widths of each sticky day header, keyed by label.
  const [headerW, setHeaderW] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        // maxGames is a RESPONSE cap, not a fetch cap - the server always pulls
        // every league across the window and Trim only limits what it returns. It
        // has to be high because Trim cuts GLOBALLY (live-first, then soonest
        // kickoff): with the strip filtered to one league, a busy Saturday could
        // otherwise push a whole NFL Sunday slate past the cut.
        const res = await fetch(`${API_BASE_URL}/games/live-scores?maxGames=150`, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Live scores request failed: ${res.status}`);
        const data = await res.json();
        const g = Array.isArray(data?.games) ? data.games : [];
        if (active) setGames(g);
      } catch (e) {
        // Silent: the strip just stays hidden/stale rather than breaking the page.
      } finally {
        clearTimeout(timeout);
        if (active) setLoaded(true);
      }
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  // Switching chips swaps the visible set under a scroll offset that belonged to
  // the previous league, and scrollX drives the sticky-header translateX. Snap
  // both back to the left edge so the "Today" pill can't sit stale.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    setScrollX(0);
  }, [league]);

  if (!loaded || !league) return null;
  // League first - a string compare prunes ~11/12 of the payload before the Date
  // math runs. MUST stay above groupByDay: groups, groupStartX and stickies all
  // derive from `visible`, which is what lets the faked sticky-header geometry
  // re-lay out from the filtered set for free.
  const visible = games.filter((g) => g.league === league && isWithinWindow(g));
  if (visible.length === 0) return null;

  const groups = groupByDay(visible);

  // Deterministic x of each group's left edge within the scroll content.
  const groupStartX = (index) => {
    let x = STRIP_INSET;
    for (let i = 0; i < index; i++) {
      const n = groups[i].games.length;
      x += n * CARD_WIDTH + (n - 1) * CARD_GAP + GROUP_GAP;
    }
    return x;
  };

  // Sticky day headers: each label pins to the left edge while its section is in
  // view (so "Today" stays put as you slide through today's games), then gets
  // pushed out by the next section's label (Sofascore-style).
  const stickies = groups.map((group, i) => {
    const naturalX = groupStartX(i);
    const w = headerW[group.label] ?? group.label.length * 10 + 24;
    let x = Math.max(STRIP_INSET, naturalX - scrollX);
    if (i < groups.length - 1) {
      const nextX = groupStartX(i + 1) - scrollX;
      x = Math.min(x, nextX - w - 12); // hand off just before the incoming label
    }
    return { label: group.label, x, live: group.games.filter((g) => g.state === 'in').length };
  });

  const measureHeader = (label) => (el) => {
    if (!el) return;
    const w = el.offsetWidth;
    setHeaderW((prev) => (prev[label] === w ? prev : { ...prev, [label]: w }));
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <style>{`@keyframes lsPulse {0%,100%{opacity:1}50%{opacity:.3}}`}</style>

      {/* Fixed row hosting the sticky day labels; overflow hidden clips a label
          once it's pushed off the left edge. */}
      <div style={{ position: 'relative', height: 24, marginBottom: 10, overflow: 'hidden' }}>
        {stickies.map((s) => (
          <div
            key={s.label}
            ref={measureHeader(s.label)}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transform: `translateX(${s.x}px)`,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>{s.label}</span>
            {s.live > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <LiveDot />
                <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 700 }}>{s.live} live</span>
              </span>
            )}
          </div>
        ))}
      </div>

      <div
        ref={scrollRef}
        onScroll={(e) => setScrollX(e.target.scrollLeft)}
        style={{ display: 'flex', overflowX: 'auto', paddingBottom: 6 }}
      >
        {groups.map((group, i) => (
          <div
            key={group.label}
            style={{ display: 'flex', gap: CARD_GAP, flex: '0 0 auto', marginRight: i < groups.length - 1 ? GROUP_GAP : 0 }}
          >
            {group.games.map((g) => (
              <GameCard key={`${g.league}-${g.id}`} game={g} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LiveScoresStrip;
